import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { FieldUpdatePayload, FieldValue } from "../../types"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"

import { SegmentAiDisclaimer } from "../segment-ai-disclaimer"
import { CompositeEditor } from "./composite-editor"
import { FieldValueView } from "./plan-field"
import { fieldToText } from "./plan-utils"

/** A leaf FieldValue has both a `value` and a trust `status`; a composite section does not. */
function isFieldValue(value: unknown): value is FieldValue {
  return typeof value === "object" && value !== null && "status" in value && "value" in value
}

/** Last-resort renderer when the caller supplies no section renderer. */
function StructuredDump({ value, testId }: { value: unknown; testId?: string }) {
  return (
    <pre
      className="text-foreground/80 max-h-64 overflow-auto rounded bg-transparent text-xs whitespace-pre-wrap"
      data-testid={testId}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

/**
 * Per-field Update review: shows Current | Suggestion side-by-side. The user can
 * Accept the suggestion as-is, Reject it, or Edit → Save & Accept (origin → manual).
 * Only Accept/Save persists (via the parent's PATCH mutation).
 *
 * `current`/`suggestion` are usually leaf FieldValues, but composite section
 * suggestions (buying/potential/engage/strengthsWeaknesses) arrive as structured
 * objects. Those are handed to `renderSection` so they display as the real
 * section — the same components the plan itself uses — rather than as raw
 * internal JSON, which was unreviewable (review P2). Composites are accepted
 * wholesale here; individual cells stay editable on the plan, where each one
 * carries its own field key.
 */
export function UpdateReviewModal({
  open,
  onOpenChange,
  title,
  current,
  suggestion,
  isLoading,
  isSaving,
  onAccept,
  renderSection,
  testIdPrefix = "sa-plan",
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  current: unknown
  suggestion: unknown
  isLoading: boolean
  isSaving: boolean
  onAccept: (payload: FieldUpdatePayload) => void
  /** Render a composite section the way the plan renders it. */
  renderSection?: (section: unknown) => ReactNode
  testIdPrefix?: string
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  // Composite sections are edited as a structured form, not free text.
  const [sectionDraft, setSectionDraft] = useState<unknown>(null)

  const suggestionField = isFieldValue(suggestion) ? suggestion : null
  const suggestionIsArray = Array.isArray(suggestionField?.value)
  const hasSuggestion = suggestion != null

  useEffect(() => {
    if (open) {
      setEditing(false)
      setDraft(fieldToText(suggestionField ?? undefined))
      setSectionDraft(null)
    }
  }, [open, suggestionField])

  const isComposite = hasSuggestion && !suggestionField
  const editableSection = sectionDraft ?? suggestion

  function acceptUnedited() {
    if (!hasSuggestion) return
    if (suggestionField) {
      onAccept({
        value: suggestionField.value,
        citations: suggestionField.citations,
        edited: false,
      })
      return
    }
    // Composite section: send the whole structured object as the value.
    onAccept({ value: suggestion, edited: false })
  }

  function saveAndAccept() {
    if (isComposite) {
      // Per-cell origin is already set by the editor, so this is sent as an
      // unedited accept: the backend preserves each incoming origin, recording
      // exactly which cells a human authored rather than flipping the whole
      // section to manual.
      onAccept({ value: editableSection, edited: false })
      return
    }
    const value = suggestionIsArray
      ? draft
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
      : draft
    onAccept({ value, citations: suggestionField?.citations ?? [], edited: true })
  }

  function renderCurrentColumn() {
    if (isFieldValue(current)) return <FieldValueView field={current} />
    if (current != null) return renderSection?.(current) ?? <StructuredDump value={current} />
    return <p className="text-muted-foreground text-sm">—</p>
  }

  function renderSuggestionColumn() {
    if (isLoading) {
      return <p className="text-muted-foreground text-sm">{t("segmentAnalysis.plan.generating")}</p>
    }
    if (editing && isComposite) {
      return (
        <>
          <CompositeEditor
            section={editableSection}
            testIdPrefix={testIdPrefix}
            onChange={setSectionDraft}
          />
          <p className="text-muted-foreground mt-2 text-xs">
            {t("segmentAnalysis.review.editHint")}
          </p>
        </>
      )
    }
    if (editing) {
      return (
        <>
          <Textarea
            className="min-h-40 text-sm"
            data-testid={`${testIdPrefix}-review-edit`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <p className="text-muted-foreground mt-2 text-xs">
            {t("segmentAnalysis.review.editHint")}
          </p>
        </>
      )
    }
    if (!hasSuggestion) {
      return <p className="text-muted-foreground text-sm">—</p>
    }
    if (!suggestionField) {
      return (
        <div data-testid={`${testIdPrefix}-review-suggestion-section`}>
          {renderSection?.(suggestion) ?? (
            <StructuredDump testId={`${testIdPrefix}-review-suggestion-json`} value={suggestion} />
          )}
        </div>
      )
    }
    return (
      // FieldValueView already renders this field's citation chips after the
      // text; the labelled block below duplicated them (review P3).
      <FieldValueView field={suggestionField} />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("segmentAnalysis.review.title")}</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>
        <DialogBody className="max-h-[60vh] overflow-y-auto">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-[0.12em] uppercase">
                {t("segmentAnalysis.review.current")}
              </h4>
              <div className="border-border bg-muted/40 rounded-md border p-3">
                {renderCurrentColumn()}
              </div>
            </div>
            <div>
              <h4 className="text-primary mb-2 text-xs font-semibold tracking-[0.12em] uppercase">
                {t("segmentAnalysis.review.suggestion")}
              </h4>
              <div className="border-primary/30 bg-primary/5 rounded-md border p-3">
                {renderSuggestionColumn()}
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="flex-wrap items-center gap-2">
          <SegmentAiDisclaimer className="mr-auto" />
          <Button
            data-testid={`${testIdPrefix}-review-reject`}
            size="sm"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            {t("segmentAnalysis.review.reject")}
          </Button>
          {editing ? (
            <Button
              data-testid={`${testIdPrefix}-review-save`}
              disabled={isSaving}
              size="sm"
              onClick={saveAndAccept}
            >
              {t("segmentAnalysis.review.saveAndAccept")}
            </Button>
          ) : (
            <>
              {hasSuggestion ? (
                <Button
                  data-testid={`${testIdPrefix}-review-edit-toggle`}
                  disabled={isLoading}
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                >
                  {t("segmentAnalysis.review.edit")}
                </Button>
              ) : null}
              <Button
                data-testid={`${testIdPrefix}-review-accept`}
                disabled={isLoading || isSaving || !hasSuggestion}
                size="sm"
                onClick={acceptUnedited}
              >
                {t("segmentAnalysis.review.accept")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
